import { Col, Input, Row, Spin } from "antd";
import queryClient from "api/queryClient";
import { useRegisterUserMutation } from "api/User/hooks.ts";
import { AuthAlert } from "components/UI/AuthAlert/AuthAlert.tsx";
import { AuthLink } from "components/UI/AuthLink/AuthLink.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { RegistrationSuccess } from "components/UI/RegistrationSuccess/RegistrationSuccess";
import { TenantAuthFormWrapper } from "components/Wrappers/TenantAuthFormWrapper/TenantAuthFormWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { Trans } from "react-i18next";
import { CheckEmailStatus, RegistrationPasswordType, RoleEnum } from "types/enums.ts";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";
import { z } from "zod";

import { useTenantCheckEmail } from "./useTenantCheckEmail";

import TenantData = App.Data.TenantData;

const EMAIL_CHECK_DEBOUNCE_MS = 600;

const schema = z.object({
    email: z.string().min(1, "forms.general-validation.email_required").email("forms.general-validation.email_invalid"),
    nickname: z.string().min(1, "forms.general-validation.nickname_required"),
});

type FormData = z.infer<typeof schema>;

interface AffiliateRegistrationFormContentProps {
    tenant: TenantData;
    isSubmitting?: boolean;
    submitError?: string[] | null;
    onEmailStatusChange?: (status: CheckEmailStatus | "idle") => void;
}

export const AffiliateRegistrationFormContent: FC<AffiliateRegistrationFormContentProps> = ({
    tenant,
    isSubmitting,
    submitError,
    onEmailStatusChange,
}) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tCommon } = usePaldockTranslation("common");
    const { t: tRegistration } = usePaldockTranslation("forms.affiliate-registration");
    const { control, fieldNames, formState, setValue } = useGeneralForm<FormData>();
    const { status, nickname, isCheckingEmail, checkEmail, resetStatus } = useTenantCheckEmail(tenant.id);
    const emailValue = useWatch({ control, name: fieldNames.email.name });
    const isEmailValid = Boolean(emailValue) && !formState.errors.email;

    useEffect(() => {
        if (!isEmailValid) {
            return;
        }
        const timer = setTimeout(() => checkEmail(emailValue, RoleEnum.AffiliatePartner), EMAIL_CHECK_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [emailValue, isEmailValid]);

    useEffect(() => {
        if (nickname) {
            setValue(fieldNames.nickname.name, nickname);
        }
    }, [nickname]);

    useEffect(() => {
        onEmailStatusChange?.(status);
    }, [status]);

    const isExistingUser =
        status === CheckEmailStatus.ExistingDifferentRole || status === CheckEmailStatus.ExistingNoAccess;
    const isSameRole = status === CheckEmailStatus.ExistingSameRole;

    return (
        <TenantAuthFormWrapper
            buttonText={tCommon("register")}
            loading={isSubmitting}
            buttonDisabled={isSameRole || status === "idle" || isSubmitting}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.email.name}
                        label={tFields("email.label")}
                        required
                        className="!mb-0"
                    >
                        <Input
                            placeholder={tFields("email.placeholder")}
                            status={isSameRole ? "error" : undefined}
                            suffix={isCheckingEmail ? <Spin size="small" /> : undefined}
                            onChange={() => resetStatus()}
                            onBlur={() => isEmailValid && checkEmail(emailValue, RoleEnum.AffiliatePartner)}
                        />
                    </FormItem>
                </Col>
                {isSameRole && (
                    <Col span={24}>
                        <AuthAlert
                            type="error"
                            message={
                                <Trans
                                    i18nKey="forms.affiliate-registration.messages.already_member"
                                    components={{
                                        a: (
                                            <AuthLink
                                                to="/$tenantSlug/auth/login"
                                                params={{ tenantSlug: tenant.slug_name }}
                                            />
                                        ),
                                    }}
                                />
                            }
                        />
                    </Col>
                )}
                {status === CheckEmailStatus.New && (
                    <Col span={24}>
                        <AuthAlert type="info" message={tRegistration("messages.new_user_info")} />
                    </Col>
                )}
                {status === CheckEmailStatus.ExistingNoAccess && (
                    <Col span={24}>
                        <AuthAlert type="warning" message={tRegistration("messages.existing_no_access_info")} />
                    </Col>
                )}
                {(status === CheckEmailStatus.New || isExistingUser) && (
                    <Col span={24}>
                        <FormItem
                            control={control}
                            name={fieldNames.nickname.name}
                            label={tFields("nickname.label")}
                            required
                            className="!mb-0"
                        >
                            <Input placeholder={tFields("nickname.placeholder")} disabled={isExistingUser} />
                        </FormItem>
                    </Col>
                )}
                {submitError && submitError.length > 0 && (
                    <Col span={24}>
                        <AuthAlert
                            type="error"
                            message={
                                submitError.length === 1 ? (
                                    submitError[0]
                                ) : (
                                    <ul className="list-disc pl-4">
                                        {submitError.map((message) => (
                                            <li key={message}>{message}</li>
                                        ))}
                                    </ul>
                                )
                            }
                        />
                    </Col>
                )}
            </Row>
        </TenantAuthFormWrapper>
    );
};

interface AffiliateRegistrationFormProps {
    tenant: TenantData;
}

export const AffiliateRegistrationForm: FC<AffiliateRegistrationFormProps> = ({ tenant }) => {
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string[] | null>(null);
    const { t } = usePaldockTranslation("forms.registration");
    const { t: tAuth } = usePaldockTranslation("auth.registration");
    const { t: tCommon } = usePaldockTranslation("common");
    // Only Content knows the checked email's status; onEmailStatusChange feeds it back here so the
    // success screen can tell an existing Paldock user (RegistrationRequestConfirmationMail — confirm
    // joining this workspace) apart from a brand new one (UserActivationMail — activate the account).
    const isExistingUserRef = useRef(false);

    const registerUserMutation = useRegisterUserMutation({
        onSuccess: () => {
            setSubmitError(null);
            setRegistrationSuccess(true);
            queryClient.invalidateQueries({ queryKey: ["me"] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
        onError: (error) => {
            setSubmitError(extractValidationErrorMessages(error));
        },
    });

    const onSubmit = (data: FormData) => {
        setSubmitError(null);
        registerUserMutation.mutate({
            email: data.email,
            nickname: data.nickname,
            type: RegistrationPasswordType.WithoutPassword,
            return_url: `${import.meta.env.VITE_APP_URL}/${tenant.slug_name}/auth/activate/{id}/{hash}`,
            role: RoleEnum.AffiliatePartner,
            tenant_id: tenant.id,
        });
    };

    if (registrationSuccess) {
        return (
            <RegistrationSuccess
                message={
                    isExistingUserRef.current
                        ? t("messages.existing_user_registration_success_message")
                        : t("messages.registration_success_message")
                }
            />
        );
    }

    return (
        <>
            <GeneralFormProvider<FormData>
                schema={schema}
                defaultValues={{ email: "", nickname: "" }}
                onSubmit={onSubmit}
                requiredMark={false}
                resetOnSubmit={false}
            >
                <AffiliateRegistrationFormContent
                    tenant={tenant}
                    isSubmitting={registerUserMutation.isPending}
                    submitError={submitError}
                    onEmailStatusChange={(status) => {
                        isExistingUserRef.current =
                            status === CheckEmailStatus.ExistingDifferentRole ||
                            status === CheckEmailStatus.ExistingNoAccess;
                    }}
                />
            </GeneralFormProvider>
            <div className="flex justify-center mt-4 text-md text-black">
                {tAuth("messages.already_have_account")}{" "}
                <AuthLink to="/$tenantSlug/auth/login" params={{ tenantSlug: tenant.slug_name }} className="ml-1">
                    {tCommon("login")}
                </AuthLink>
            </div>
        </>
    );
};
